/** Add-on / connector logos: bundled assets under /assets/addons, Deubner under /assets. */

import { connectorAddonSlugFromRid } from './integration-addon.util';

export interface AddonLogoMeta {
  logoUrl?: string;
  blockColor: string;
}

const DEFAULT_BLOCK_COLOR = '#71717A';
export const ADDONS_ASSET_BASE = '/assets/addons';
const IMAGE_URL_PATTERN = /\.(png|jpe?g|gif|svg|webp|ico)(\?|#|$)/i;

/** Bundled logos keyed by connector / Stripe integration slug (lowercase). */
const BUNDLED_ADDON_LOGO_FILES: Record<string, string> = {
  kleos: 'kleos.svg',
  jurnodes: 'jurnodes.svg',
  actaport: 'actaport.svg',
  advoware: 'advoware.svg',
  datev: 'datev.svg',
  docuware: 'docuware.svg',
  imanage: 'iManage.svg',
  renostar: 'renostar.svg',
  'ra-micro': 'ra-micro.svg',
  'otto-schmidt': 'otto-schmidt.svg',
  sharepoint: 'sharepoint.svg',
  onedrive: 'onedrive.svg',
};

/** Frontend border colors — overrides stale DB manifest accentColor until connector re-sync. */
const BUNDLED_ADDON_BORDER_COLORS: Record<string, string> = {
  actaport: '#05003b',
  advoware: '#89cdc7',
  datev: '#90d033',
  'otto-schmidt': '#003087',
  docuware: '#303AB2',
};

const DEUBNER_LOGO: AddonLogoMeta = {
  logoUrl: '/assets/deubner-logo.png',
  blockColor: '#2563EB',
};

function isRemoteImageUrl(value: string): boolean {
  return (
    (value.startsWith('http://') || value.startsWith('https://'))
    && IMAGE_URL_PATTERN.test(value)
  );
}

function isBundledAssetRef(value: string): boolean {
  return !value.startsWith('http://') && !value.startsWith('https://');
}

function normalizeBundledAssetFilename(filename: string): string {
  // Legacy .ico refs were migrated to SVG; keep real PNG/JPEG/WebP filenames as-is.
  return filename.replace(/\.ico$/i, '.svg');
}

function bundledLogoUrl(filename: string): string {
  return `${ADDONS_ASSET_BASE}/${normalizeBundledAssetFilename(filename).replace(/^\/+/, '')}`;
}

/** Bundled logo URL for a connector / Stripe slug, if one is shipped under /assets/addons. */
export function bundledAddonLogoUrl(slug: string): string | undefined {
  const file = BUNDLED_ADDON_LOGO_FILES[slug.trim().toLowerCase()];
  return file ? bundledLogoUrl(file) : undefined;
}

function resolveManifestIconUrl(icon: string | undefined | null): string | undefined {
  const trimmed = icon?.trim();
  if (!trimmed) {
    return undefined;
  }
  if (isRemoteImageUrl(trimmed)) {
    return trimmed;
  }
  if (isBundledAssetRef(trimmed)) {
    return bundledLogoUrl(trimmed);
  }
  return undefined;
}

function resolveBrandingLogoUrl(
  branding: Record<string, unknown>,
): string | undefined {
  const icon = typeof branding['icon'] === 'string' ? branding['icon'] : undefined;
  const logo = typeof branding['logo'] === 'string' ? branding['logo'] : undefined;

  return resolveManifestIconUrl(icon) ?? resolveManifestIconUrl(logo);
}

function bundledBlockColor(slug: string, manifestColor: string): string {
  return BUNDLED_ADDON_BORDER_COLORS[slug.trim().toLowerCase()] ?? manifestColor;
}

export function addonLogoFromManifest(
  manifest: Record<string, unknown> | null | undefined,
  connectorRid?: string | null,
): AddonLogoMeta | undefined {
  const branding = manifest?.['branding'] as Record<string, unknown> | undefined;
  if (!branding && !connectorRid) {
    return undefined;
  }

  const slug = connectorRid ? connectorAddonSlugFromRid(connectorRid) : undefined;
  const manifestColor =
    typeof branding?.['accentColor'] === 'string' && branding['accentColor'].trim()
      ? branding['accentColor'].trim()
      : DEFAULT_BLOCK_COLOR;
  const blockColor = slug ? bundledBlockColor(slug, manifestColor) : manifestColor;

  const bundledLogo = slug ? bundledAddonLogoUrl(slug) : undefined;
  const logoUrl =
    bundledLogo
    ?? (branding ? resolveBrandingLogoUrl(branding) : undefined);

  return {
    logoUrl,
    blockColor,
  };
}

export function deubnerAddonLogo(): AddonLogoMeta {
  return DEUBNER_LOGO;
}

export function addonLogoAbbreviation(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0] ?? ''}${words[1][0] ?? ''}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}
